import type { DocumentFormat, ExtensionMap } from '../models/viewer-types';
import { prefersText } from './text-format';

type NamedFormat = Exclude<DocumentFormat, 'auto'>;

/** What a name resolves to: the format to preview it as, and the syntax language to highlight by. */
export interface ExtensionRule {
  format: DocumentFormat;
  language: string;
}

const formats = new Set<string>([
  'zip',
  'msp360-log-archive',
  'xlsx',
  'docx',
  'pdf',
  'image',
  'markdown',
  'json',
  'xml',
  'html',
  'code',
  'text',
  'msp360-log'
]);

const imageName =
  /\.(?:png|apng|jpe?g|jpe|pjpeg|jfif|gif|webp|avif|svg|bmp|dib|ico|cur|tiff?|heic|heif|jxl|jp2|j2k|jpf|dng|cr2|cr3|nef|arw|raf|orf|rw2|psd)$/i;

const ownFormats: Array<NamedFormat> = ['zip', 'pdf', 'docx', 'xlsx', 'json'];

/**
 * Refuses a map the host application cannot have meant: an unknown format, a path instead of an
 * extension, a language that is not text.
 *
 * @param {ExtensionMap} [map] The map to check.
 */
export function validateExtensionMap(map: ExtensionMap = {}): void {
  if (!map || typeof map !== 'object' || Array.isArray(map)) throw Error('extensionMap must be an object.');

  for (const [extension, value] of Object.entries(map)) {
    const rule = typeof value === 'string' ? { format: value, language: undefined } : value;

    if (
      !extension.replace(/^\./, '') ||
      /[\\\s?#/]/.test(extension) ||
      !rule ||
      !formats.has(rule.format) ||
      (rule.language != null && typeof rule.language !== 'string')
    ) {
      throw Error(`Invalid extension mapping: ${extension}`);
    }
  }
}

// Matches literal fragments one by one rather than building a regular expression that could backtrack.
function matchesPattern(name: string, pattern: string): boolean {
  const parts = pattern.split('*');

  if (!name.startsWith(parts[0])) return false;

  let offset = parts[0].length;

  for (const part of parts.slice(1, -1)) {
    const index = name.indexOf(part, offset);

    if (index < 0) return false;

    offset = index + part.length;
  }

  const tail = parts.at(-1) as string;

  return name.length - tail.length >= offset && name.endsWith(tail);
}

// Literal suffixes win over patterns, and among patterns the one spelling out more characters wins.
function priority(key: string): [number, number] {
  return [key.includes('*') ? 0 : 1, key.replaceAll('*', '').length];
}

/**
 * How a document named like this is to be previewed.
 *
 * @param {string} name Name of the document.
 * @param {ExtensionMap} [map] Associations the host application has set.
 * @param {DocumentFormat} [format] A format the caller insists on, which ends the search.
 * @param {string} [language] The syntax language to fall back to.
 * @return {ExtensionRule} The format, `auto` when the name decides nothing.
 */
export function resolveExtension(name: string, map: ExtensionMap = {}, format: DocumentFormat = 'auto', language = ''): ExtensionRule {
  if (format !== 'auto') return { format, language };

  const lower = (name.toLowerCase().split(/[\\/]/).pop() as string) || '';
  const byPriority = Object.entries(map).sort((left, right) => {
    const [leftLiteral, leftLength] = priority(left[0]);
    const [rightLiteral, rightLength] = priority(right[0]);

    return rightLiteral - leftLiteral || rightLength - leftLength;
  });

  for (const [key, value] of byPriority) {
    const pattern = key.toLowerCase();
    const matches = pattern.includes('*')
      ? matchesPattern(lower, pattern.startsWith('.') ? `*${pattern}` : pattern)
      : lower.endsWith(pattern.startsWith('.') ? pattern : `.${pattern}`);

    if (matches) {
      const rule = typeof value === 'string' ? { format: value, language: undefined } : value;

      return { format: rule.format, language: rule.language ?? language };
    }
  }

  const extension = lower.split('.').pop() as NamedFormat;

  if (ownFormats.includes(extension)) return { format: extension, language };

  if (imageName.test(lower)) return { format: 'image', language };

  // Text keeps its automatic detection: that is also what picks the syntax language out of the name.
  return { format: 'auto', language };
}

/**
 * Whether a document named like this can be shown at all.
 *
 * @param {string} name Name of the document.
 * @param {ExtensionMap} [map] Associations the host application has set.
 * @return {boolean} True when some format claims the name, or when it reads as text.
 */
export const canPreviewName = (name: string, map: ExtensionMap = {}): boolean => {
  const rule = resolveExtension(name, map);

  return rule.format !== 'auto' || prefersText(name);
};
